/**
 * User Settings Service
 * Flutter source: lib/data/repository/settins_repository_impl.dart
 *
 * Reads user settings from local encrypted storage (IndexedDB)
 * and handles toggle with optimistic update + cloud function call.
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { UserSettingStorage } from '@/domain/entities/secureStorage/secureStorageEntities';

export class UserSettingsService {
  private storage: SecureStorageWebCrypto;

  constructor(storage?: SecureStorageWebCrypto) {
    this.storage = storage || SecureStorageWebCrypto.getInstance();
  }

  /**
   * Toggle a user setting with optimistic local update.
   * Mirrors Flutter's SettingsRepositoryImpl.toggleUserSetting():
   * 1. Read setting from local storage
   * 2. Flip value (0<->1), save to storage
   * 3. Call cloud function
   * 4. On failure: revert to original value
   */
  async toggleUserSetting(settingName: string): Promise<boolean> {
    const key = SECURE_STORAGE_KEYS.USER_SETTINGS + settingName;
    const currentSetting = await this.storage.getEncrypted<UserSettingStorage>(key);

    if (!currentSetting) {
      return false;
    }

    const originalValue = currentSetting.value ?? currentSetting.defaultValue;
    const newValue = originalValue === 1 ? 0 : 1;

    // Optimistic local update
    const updatedSetting: UserSettingStorage = { ...currentSetting, value: newValue };
    await this.storage.setEncrypted(key, updatedSetting);

    // Call cloud function
    const success = await cloudFunctionsService.toggleUserSetting(settingName);

    if (!success) {
      // Revert on failure
      const revertedSetting: UserSettingStorage = { ...currentSetting, value: originalValue };
      await this.storage.setEncrypted(key, revertedSetting);
    }

    return success;
  }
}
